/**
 * @file AddSuppliedQuantityModal.js
 * @version 1.9.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-23
 * @modified 2026-10-22
 * @description Modal for setting supplied quantities and vehicle assignment on order lines.
 *
 *              On a THIRD-PARTY DELIVERY order it is "Update Delivery" instead:
 *              the delivery status and the supplied quantities in one step,
 *              since an outside haulier's delivery is not ours to see
 *              (services/orderDeliveryService.js). Status defaults to Delivered
 *              and each empty quantity to what was ordered; Not Despatched
 *              takes the quantities away, as nothing has been supplied yet. It
 *              can be reopened to change either until the order is invoiced,
 *              or a linked PO-D is billed. There is no vehicle column — the
 *              vehicle is the haulier's.
 *
 * 1.5.0 (2026-10-08, Cam): supplied quantities may change after delivery
 * until the order's invoice has GONE OUT to the customer (emailed, on a
 * delivered statement, or dated before this month — the server's
 * utils/invoiceGoneOut). An order that is not third-party is fixed only by
 * that (`suppliedBlock`); Update Delivery keeps its billed-PO-D rule too
 * (`block`). When the order has an invoice raised on the old quantity that
 * has not gone out, the server offers its changes (409
 * INVOICE_CHANGES_PROPOSED): InvoiceProposalDialog — "Apply these changes and
 * save" saves and edits the invoice, "Continue editing" saves nothing. What
 * happened to the invoice is said before the window closes.
 * 1.5.1 (2026-10-08, review): an invoice gone out fixes the QUANTITIES only —
 * who delivered it can still be corrected and saved; an invoiced order offers
 * "Update invoice from supplied qty…" here too.
 * 1.6.0 (2026-10-08, Cam): after Update Delivery, says what happened to the
 * order's draft invoice for pricing (raised, updated or deleted).
 * 1.7.0 (2026-10-12, Cam): "Delivered by" is TransportStack — an empty cell
 * asks for the driver first, with "No driver".
 * 1.8.0 (2026-10-13, Cam): who delivered a line on a planning board is the
 * board's to set up to and including its delivery date — the cell is
 * read-only here with the date it opens (server: utils/transportLock, which
 * refuses it too); from the day after it is a correction, and the board's
 * record of that day is corrected with it. Anything the server did not take
 * is said after the save.
 * 1.8.1 (2026-10-13, review): who may change it follows the Edit Transport
 * Planning permission of the user (`user`), not whether the transport lists
 * loaded; a Direct Delivery or third-party line's cells are greyed with the
 * reason (someone else carries it — the server records nothing of ours).
 *
 * 1.8.2 (2026-10-20, Cam): Ordered shows in the line's own unit with its
 * tonnes (QtyWithTonnes), and a supplied quantity typed for a pack line is
 * read back under the input ("38 × 25kg bag · 0.95 T").
 *
 * 1.9.0 (2026-10-22, Cam): COLLECTED. A third-party order from our stock
 * (the server's `collection.applies`) is "Collection & Delivery": its first
 * status is "Collected — despatched" with a Collected date (today by
 * default, not in the future, not after the delivery date) and the collected
 * quantities, saved before the delivery date. The dates then move it on —
 * In transit on the delivery date, Delivered after, with these quantities
 * (server: utils/orderCollection). Not despatched clears the date too.
 * Before migrations/third_party_collected_date.sql the date is greyed "Not
 * ready" and Collected is not offered.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import './AddSuppliedQuantityModal.css';
import Input from './Input';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import { formatQuantity, formatDecimalInput } from '../utils/numberFormatters';
import QtyWithTonnes from './QtyWithTonnes';
import { quantityWithTonnes, qtyFactsOf } from '../utils/qtyWithTonnes';
import TransportStack from './TransportStack';
import { CUSTOMER_COLLECTION_LABEL } from '../utils/deliveryTerms';
import AlertDialog from './AlertDialog';
import UpdateInvoiceFromSuppliedDialog from './UpdateInvoiceFromSuppliedDialog';
import InvoiceProposalDialog from './InvoiceProposalDialog';
import { isInvoiceQuestion, applyAnswer, invoiceUpdateMessage } from '../utils/invoiceProposals';
import { draftInvoiceMessage } from '../utils/draftPricing';
import { hasPermission } from '../utils/permissions';

// Values are the stored delivery statuses; "In-Transit" reads as despatched.
const DELIVERY_OPTIONS = [
  { value: 'Delivered', label: 'Delivered' },
  { value: 'In-Transit', label: 'Despatched (in transit)' },
  { value: 'Not Despatched', label: 'Not despatched' }
];
// From our stock, collected by the haulier before the delivery date (1.9.0).
const COLLECTED = 'Despatched';
const COLLECTION_OPTIONS = [
  { value: COLLECTED, label: 'Collected — despatched' },
  { value: 'In-Transit', label: 'In transit' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Not Despatched', label: 'Not despatched' }
];
const todayLondon = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
const ukDay = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');

function AddSuppliedQuantityModal({ isOpen, onClose, orderId, onSuccess, user = null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [quantities, setQuantities] = useState({});
  // Who delivered each line — haulier, driver, vehicle — recorded with what
  // was supplied, with the planning boards' auto-fill (utils/transportAutofill).
  // Editable for those who may change transport planning (the lists load);
  // shown read-only to everyone else, and the server ignores it from them.
  const [transportSelections, setTransportSelections] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [hauliers, setHauliers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // Whether the transport lists loaded (the fallback when no user is given).
  const [listsLoaded, setListsLoaded] = useState(false);
  // The permission decides (the lists are only its options).
  const transportAllowed = user ? hasPermission(user, 'transport_planning') : listsLoaded;
  const [error, setError] = useState(null);
  const [imbalanceWarning, setImbalanceWarning] = useState(null);
  // Third-party delivery orders only (see header).
  const [delivery, setDelivery] = useState(null); // { thirdParty, manual, block, ... }
  const [deliveryStatus, setDeliveryStatus] = useState('Delivered');
  const [collectedDate, setCollectedDate] = useState('');
  const thirdParty = !!delivery?.thirdParty;
  // Collected from our stock by the haulier (server: delivery.collection).
  const collect = thirdParty && !!delivery?.collection?.applies;
  const collectReady = collect && !!delivery?.collection?.ready;
  const collecting = collect && deliveryStatus === COLLECTED;
  const deliveryDay = delivery?.deliveryDate || null;
  // The latest a collection can be: today, or the delivery date if earlier.
  const collectMax = (() => { const t = delivery?.today || todayLondon(); return deliveryDay && deliveryDay < t ? deliveryDay : t; })();
  // What fixes the quantities: on Update Delivery, an invoice gone out or a
  // billed PO-D; otherwise only an invoice gone out (2026-10-08).
  const block = thirdParty ? (delivery?.block || null) : (delivery?.suppliedBlock || null);
  // The server's invoice question, and what it said about the invoice after.
  const [invoiceQuestion, setInvoiceQuestion] = useState(null);
  const [doneNote, setDoneNote] = useState(null);
  const [invoiceOffer, setInvoiceOffer] = useState(false);
  // The customer collected it: haulier, driver and vehicle read the label (server: deliveryState).
  const customerCollection = !!delivery?.customerCollection;
  const canSupply = !thirdParty || deliveryStatus !== 'Not Despatched';

  // Recalculate slideout button widths whenever footer content changes
  useSlideOutButton([loading, saving, imbalanceWarning, thirdParty, canSupply, order?.invoiced_status, collect]);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);

    try {
      const [orderRes, vehicleRes, deliveryRes, haulierRes, driverRes] = await Promise.all([
        fetch(`${API_URL}/api/orders/customer-orders/${orderId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/transport/vehicles`, { credentials: 'include' }),
        fetch(`${API_URL}/api/orders/customer-orders/${orderId}/delivery`, { credentials: 'include' }),
        fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' }),
        fetch(`${API_URL}/api/transport/drivers`, { credentials: 'include' })
      ]);

      if (!orderRes.ok) throw new Error('Failed to load order');
      const data = await orderRes.json();
      setOrder(data);

      const state = deliveryRes.ok ? await deliveryRes.json() : null;
      setDelivery(state);
      // What they set last time, else Delivered — the usual reason to be here.
      // From our stock before (or on) its delivery date: Collected, today.
      const collects = !!(state?.thirdParty && state?.collection?.applies && state?.collection?.ready);
      const today = state?.today || todayLondon();
      const initialStatus = state?.manual
        || (collects && (!state?.deliveryDate || state.deliveryDate >= today) ? COLLECTED : 'Delivered');
      setDeliveryStatus(initialStatus);
      setCollectedDate(state?.collection?.collectedDate
        || (collects && initialStatus === COLLECTED ? (state?.deliveryDate && state.deliveryDate < today ? state.deliveryDate : today) : ''));

      // Initialize quantities and vehicle selections from existing data. On a
      // third-party order being marked despatched/delivered, an empty quantity
      // starts at what was ordered.
      const initialQty = {};
      const initialTransport = {};
      if (data.lines) {
        data.lines.forEach(line => {
          initialQty[line.id] = line.supplied_quantity == null && state?.thirdParty && initialStatus !== 'Not Despatched'
            ? formatDecimalInput(line.quantity)
            : formatDecimalInput(line.supplied_quantity);
          initialTransport[line.id] = { haulier: line.haulier || '', driver: line.driver || '', vehicle: line.vehicle || '' };
        });
      }
      setQuantities(initialQty);
      setTransportSelections(initialTransport);

      setListsLoaded(vehicleRes.ok && haulierRes.ok && driverRes.ok);
      if (vehicleRes.ok) {
        const vehicleData = await vehicleRes.json();
        if (vehicleData.success) setVehicles(vehicleData.vehicles.map(v => v.registration));
      }
      if (haulierRes.ok) {
        const h = await haulierRes.json();
        if (h.success) setHauliers((h.hauliers || []).map(x => x.name));
      }
      if (driverRes.ok) {
        const d = await driverRes.json();
        if (d.success) setDrivers(d.drivers || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (isOpen && orderId) {
      loadOrder();
    }
  }, [isOpen, orderId, loadOrder]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setOrder(null);
        setQuantities({});
        setTransportSelections({});
        setError(null);
        setImbalanceWarning(null);
        setDelivery(null);
        setDeliveryStatus('Delivered');
        setCollectedDate('');
        setLoading(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleQuantityChange = (lineId, value) => {
    setQuantities(prev => ({ ...prev, [lineId]: value }));
  };

  // Marking it despatched or delivered fills any empty quantity with what was
  // ordered; the user changes whichever were short.
  const handleDeliveryStatusChange = (value) => {
    setDeliveryStatus(value);
    // Collected: the date it went, today unless already recorded.
    if (value === COLLECTED && !collectedDate) setCollectedDate(collectMax);
    if (value !== 'Not Despatched' && order?.lines) {
      setQuantities(prev => {
        const next = { ...prev };
        order.lines.forEach(line => {
          if (next[line.id] === '' || next[line.id] === undefined) next[line.id] = formatDecimalInput(line.quantity);
        });
        return next;
      });
    }
  };

  const handleTransportChange = (lineId, changes) => {
    setTransportSelections(prev => ({ ...prev, [lineId]: { ...(prev[lineId] || {}), ...changes } }));
  };

  const handleSetAllToOrdered = () => {
    if (!order?.lines) return;
    const updated = {};
    order.lines.forEach(line => {
      updated[line.id] = formatDecimalInput(line.quantity);
    });
    setQuantities(updated);
  };

  // After a save: say what happened to the invoice first, if anything.
  const finish = (data) => {
    // And what happened to the order's draft invoice for pricing (1.6.0), and
    // any change of who delivered it the server did not take (1.8.0).
    const refusedTransport = Array.isArray(data?.transport_refused) && data.transport_refused.length
      ? `Who delivered it was not changed — ${data.transport_refused.join('; ')}. The quantities were saved.` : null;
    const note = [data?.collection_note || null, invoiceUpdateMessage(data?.invoice_update), draftInvoiceMessage(data?.draft_invoice), refusedTransport].filter(Boolean).join(' ');
    if (note) { setDoneNote({ message: note, type: data?.invoice_update?.refused?.length || refusedTransport ? 'warning' : 'success' }); return; }
    onSuccess?.();
  };

  const handleSave = async (invoiceAnswer = null) => {
    setSaving(true);
    setError(null);

    try {
      if (thirdParty) {
        const lines = (order?.lines || []).map(line => {
          const raw = quantities[line.id];
          const qty = raw === '' || raw === undefined ? null : parseFloat(raw);
          if (qty !== null && (isNaN(qty) || qty < 0)) throw new Error('Supplied quantity cannot be negative');
          return { lineId: line.id, suppliedQuantity: qty };
        });
        const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/delivery`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: deliveryStatus,
            lines,
            // The collected date goes with any status but Not despatched (which clears it).
            ...(collectReady && deliveryStatus !== 'Not Despatched' ? { collectedDate: collectedDate || null } : {}),
            ...(invoiceAnswer ? { invoice_changes: invoiceAnswer } : {})
          })
        });
        const data = await res.json();
        if (isInvoiceQuestion(res.status, data)) {
          setInvoiceQuestion({ proposals: data.proposals || [], notices: data.notices || [], stale: !!data.stale });
          return;
        }
        if (!res.ok) throw new Error(data.error || 'Failed to update delivery');
        finish(data);
        return;
      }

      const lines = Object.entries(quantities)
        .filter(([_, val]) => val !== '' && val !== undefined)
        .map(([lineId, val]) => ({
          lineId: parseInt(lineId),
          suppliedQuantity: parseFloat(val),
          // Transport only from someone who may change it; left out, the
          // server leaves each field as it is. Never on Customer Collection:
          // the customer carried it.
          ...(transportAllowed && !customerCollection && delivery?.transport?.[lineId]?.phase !== 'board'
            && !delivery?.transport?.[lineId]?.carriedElsewhere ? {
            haulier: transportSelections[lineId]?.haulier || null,
            driver: transportSelections[lineId]?.driver || null,
            vehicle: transportSelections[lineId]?.vehicle || null
          } : {})
        }));

      if (lines.length === 0) {
        throw new Error('Please enter at least one supplied quantity');
      }

      // Validate no negative values
      for (const line of lines) {
        if (isNaN(line.suppliedQuantity) || line.suppliedQuantity < 0) {
          throw new Error('Supplied quantity cannot be negative');
        }
      }

      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/supplied-quantity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lines, ...(invoiceAnswer ? { invoice_changes: invoiceAnswer } : {}) })
      });

      const data = await res.json();
      if (isInvoiceQuestion(res.status, data)) {
        setInvoiceQuestion({ proposals: data.proposals || [], notices: data.notices || [], stale: !!data.stale });
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      const invoiceNote = invoiceUpdateMessage(data.invoice_update);

      // Check split family balance if this order has splits
      if (order?.parent_order_id || order?.has_splits) {
        try {
          const familyRes = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/split-family`, {
            credentials: 'include'
          });
          const familyData = await familyRes.json();

          if (familyData.success && !familyData.isBalanced) {
            const unbalancedLines = familyData.lines.filter(l => !l.balanced);
            const details = unbalancedLines.map(l =>
              `${l.productName}: supplied ${formatQuantity(l.suppliedQuantity)}, allocated ${formatQuantity(l.totalAllocated)} (${l.unallocated > 0 ? `${formatQuantity(l.unallocated)} unallocated` : `${formatQuantity(Math.abs(l.unallocated))} over-allocated`})`
            ).join('\n');
            setImbalanceWarning({
              message: `The supplied quantity has been updated, but the total quantities across the parent and child orders no longer match the supplied quantity.${invoiceNote ? ` ${invoiceNote}` : ''}`,
              details,
              familyData
            });
            return; // Don't close - show warning first
          }
        } catch (err) {
          console.error('Failed to check split family balance:', err);
        }
      }

      finish(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!order?.lines) return false;
    // Never set by hand yet: saving is itself the change.
    if (thirdParty && deliveryStatus !== delivery?.manual) return true;
    if (collectReady && deliveryStatus !== 'Not Despatched' && (collectedDate || null) !== (delivery?.collection?.collectedDate || null)) return true;
    return order.lines.some(line => {
      const currentQty = quantities[line.id];
      const originalQty = formatDecimalInput(line.supplied_quantity);
      const t = transportSelections[line.id] || {};
      const transportChanged = !customerCollection
        && ['haulier', 'driver', 'vehicle'].some(f => (t[f] || '') !== (line[f] || ''));
      return currentQty !== originalQty || transportChanged;
    });
  };

  const handleDismissWarning = () => {
    setImbalanceWarning(null);
    onSuccess?.();
  };

  const renderFooter = () => {
    if (imbalanceWarning) {
      return (
        <button className="btn-slideout" onClick={handleDismissWarning}>
          <div className="icon-wrapper">
            <span className="material-symbols-rounded">tune</span>
          </div>
          <span className="btn-label">OK, I'll Adjust Quantities</span>
        </button>
      );
    }

    return (
      <>
        <div style={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between", gap: "var(--spacing-sm)" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "var(--spacing-sm)" }}>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">close</span>
              </div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "var(--spacing-sm)" }}>
            {order?.invoiced_status === 'Invoiced' && (
              <button className="btn-slideout" data-write-action onClick={() => setInvoiceOffer(true)} disabled={saving}
                      title="The changes the invoice still needs for the supplied quantities (until it has gone out)">
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">receipt_long</span>
                </div>
                <span className="btn-label">Update invoice from supplied qty…</span>
              </button>
            )}
            <button
              className="btn-slideout"
              onClick={handleSetAllToOrdered}
              disabled={saving || !canSupply || !!block}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">done_all</span>
              </div>
              <span className="btn-label">Set All to Ordered</span>
            </button>
            <button
              className="btn-slideout btn-slideout-primary"
              onClick={() => handleSave()}
              disabled={saving || !hasChanges() || (thirdParty && !!block) || (collecting && !collectedDate)}
              title={collecting && !collectedDate ? 'Enter the date the haulier collected it' : undefined}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">{saving ? 'progress_activity' : 'save'}</span>
              </div>
              <span className="btn-label">{saving ? 'Saving...' : (collecting ? 'Record collection' : 'Save')}</span>
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={collect ? 'Collection & Delivery' : thirdParty ? 'Update Delivery' : 'Add Supplied Quantity'}
      size="lg"
      footer={!loading ? renderFooter() : null}
      className="add-supplied-qty-modal"
    >
      {loading ? (
        <div className="loading-state">
          <LoadingSpinner />
        </div>
      ) : imbalanceWarning ? (
        <div className="supplied-qty-content">
          <div className="imbalance-warning">
            <div className="imbalance-header">
              <span className="material-symbols-rounded">warning</span>
              <span>Quantity Imbalance Detected</span>
            </div>
            <p>{imbalanceWarning.message}</p>
            <div className="imbalance-details">
              {imbalanceWarning.familyData?.lines?.filter(l => !l.balanced).map((line, i) => (
                <div key={i} className="imbalance-line">
                  <span className="product-name">{line.productName}</span>
                  <div className="imbalance-breakdown">
                    <span>Supplied: <strong>{formatQuantity(line.suppliedQuantity)}</strong></span>
                    <span>Parent: {formatQuantity(line.parentQuantity)}</span>
                    {line.childQuantities.map((cq, j) => (
                      <span key={j}>{cq.orderNumber}: {formatQuantity(cq.quantity)}</span>
                    ))}
                    <span className={line.unallocated > 0 ? 'unallocated' : 'over-allocated'}>
                      {line.unallocated > 0
                        ? `${formatQuantity(line.unallocated)} unallocated`
                        : `${formatQuantity(Math.abs(line.unallocated))} over-allocated`
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <p className="imbalance-hint">
              You can adjust this by editing the parent or child order quantities, deleting a child split order, or creating a new split to allocate the remaining quantity.
            </p>
          </div>
        </div>
      ) : (
        <div className="supplied-qty-content">
          {error && <div className="error-message">{error}</div>}
          {!thirdParty && block && <div className="error-message">{block} Who delivered it can still be corrected.</div>}

          {thirdParty && (
            <div className="delivery-status-row">
              <div className="delivery-status-field">
                <label>Delivery status</label>
                <Dropdown
                  value={deliveryStatus}
                  onChange={handleDeliveryStatusChange}
                  options={collectReady ? COLLECTION_OPTIONS : DELIVERY_OPTIONS}
                  disabled={!!delivery?.block}
                />
              </div>
              {/* From our stock: the day the haulier collected it, beside the
                  delivery date (1.9.0). */}
              {collect && deliveryStatus !== 'Not Despatched' && (
                <div className="delivery-status-field collected-date-field"
                     title={!collectReady ? delivery?.collection?.notReady : `Not in the future, and not after the delivery date${deliveryDay ? ` (${ukDay(deliveryDay)})` : ''}`}>
                  <label>Collected{collecting ? '' : ' (optional)'}</label>
                  <DateInput
                    value={collectReady ? collectedDate : ''}
                    onChange={(e) => setCollectedDate(e.target.value || '')}
                    max={collectMax}
                    disabled={!collectReady || !!delivery?.block}
                  />
                </div>
              )}
              {collect && deliveryDay && (
                <div className="delivery-status-field delivery-date-readout">
                  <label>Delivery</label>
                  <span className="delivery-date-value">{ukDay(deliveryDay)}</span>
                </div>
              )}
              <p className="delivery-status-note">
                {delivery?.block
                  ? delivery.block
                  : collect && !collectReady
                    ? `Collected date: ${delivery?.collection?.notReady || 'not ready'}. Set how the delivery went as before.`
                  : collecting
                    ? `Collected from our stock: enter what the haulier took. It reads In transit on ${deliveryDay ? ukDay(deliveryDay) : 'the delivery date'} and Delivered the day after, with these quantities — nothing more to enter.`
                  : canSupply
                    ? 'Third-party delivery: set how it went, and what was supplied if it differs from the order.'
                    : `Nothing is supplied until it has been despatched — saving clears any supplied quantities${collect ? ' and the collected date' : ''}.`}
              </p>
            </div>
          )}

          <div className="order-info">
            <span className="order-number">{order?.order_number}</span>
            <span className="customer-name">{order?.customer_details?.company_name}</span>
          </div>


          <div className="table-wrapper" style={{ overflow: "visible" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  {!thirdParty && <th className="vehicle-col">Delivered by</th>}
                  <th className="text-right">Ordered Qty</th>
                  <th className="text-right">{collecting ? 'Collected Qty' : 'Supplied Qty'}</th>
                </tr>
              </thead>
              <tbody>
                {order?.lines?.map(line => (
                  <tr key={line.id}>
                    <td>
                      {line.product_code && (
                        <span className="product-code">{line.product_code}</span>
                      )}
                      {line.product_name}
                    </td>
                    {!thirdParty && customerCollection && <td className="vehicle-col">
                      {/* The customer collected it: shown, never sent. */}
                      <div className="transport-stack" title="The customer collects this order — there is no haulier, driver or vehicle">
                        {['Haulier', 'Driver', 'Vehicle'].map(what => (
                          <SearchableDropdown
                            key={what}
                            options={[{ value: CUSTOMER_COLLECTION_LABEL, label: CUSTOMER_COLLECTION_LABEL }]}
                            value={CUSTOMER_COLLECTION_LABEL}
                            onChange={() => {}}
                            placeholder={what}
                            disabled
                          />
                        ))}
                      </div>
                    </td>}
                    {!thirdParty && !customerCollection && <td className="vehicle-col">
                      {/* An empty cell asks for the driver first, with "No
                          driver" (TransportStack — the PO editor's cell too). */}
                      <TransportStack
                        haulier={transportSelections[line.id]?.haulier || ''}
                        driver={transportSelections[line.id]?.driver || ''}
                        vehicle={transportSelections[line.id]?.vehicle || ''}
                        onChange={(changes) => handleTransportChange(line.id, changes)}
                        hauliers={hauliers}
                        drivers={drivers}
                        vehicles={vehicles}
                        disabled={!transportAllowed || !canSupply || delivery?.transport?.[line.id]?.phase === 'board'
                          || !!delivery?.transport?.[line.id]?.carriedElsewhere}
                        title={delivery?.transport?.[line.id]?.carriedElsewhere
                          ? delivery.transport[line.id].carriedElsewhere
                          : delivery?.transport?.[line.id]?.phase === 'board'
                          ? delivery.transport[line.id].reason
                          : !transportAllowed ? 'Changing who delivered it needs Edit Transport Planning'
                            : delivery?.transport?.[line.id]?.phase === 'correction'
                              ? 'The delivery day has passed: a change here corrects the record, and the planning board\'s record of that day'
                              : undefined}
                        blank={{ haulier: 'None', driver: 'Driver', vehicle: 'None' }}
                      />
                    </td>}
                    {/* Ordered in the line's own unit with its tonnes, and
                        what is typed for supplied read back the same way
                        beneath the input (utils/qtyWithTonnes — Cam,
                        20 Oct 2026); the input keeps what is typed. */}
                    <td className="text-right"><QtyWithTonnes quantity={line.quantity} line={line} /></td>
                    <td className="text-right">
                      <Input
                        type="number"
                        className="qty-input styled-input"
                        value={canSupply ? (quantities[line.id] || '') : ''}
                        onChange={(e) => handleQuantityChange(line.id, e.target.value)}
                        placeholder={canSupply ? '0.00' : '—'}
                        disabled={!canSupply || !!block}
                        min="0"
                      />
                      {canSupply && (() => {
                        const v = quantityWithTonnes(quantities[line.id], { ...qtyFactsOf(line), pallets: null });
                        return v.kind === 'packs' ? <span className="supplied-tonnes-hint">{v.main} · {v.tonnesText}</span> : null;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {invoiceQuestion && (
        <InvoiceProposalDialog
          proposals={invoiceQuestion.proposals}
          notices={invoiceQuestion.notices}
          stale={invoiceQuestion.stale}
          busy={saving}
          onApply={() => {
            const q = invoiceQuestion;
            setInvoiceQuestion(null);
            handleSave(applyAnswer(q.proposals));
          }}
          onContinue={() => setInvoiceQuestion(null)}
        />
      )}
      {invoiceOffer && orderId && (
        <UpdateInvoiceFromSuppliedDialog
          baseUrl={`${API_URL}/api/orders/customer-orders/${orderId}/invoice-proposals`}
          onClose={() => setInvoiceOffer(false)}
          onDone={(message, type) => setDoneNote({ message, type: type || 'success' })}
        />
      )}
      <AlertDialog
        isOpen={!!doneNote}
        type={doneNote?.type || 'info'}
        message={doneNote?.message || ''}
        onClose={() => { setDoneNote(null); onSuccess?.(); }}
      />
    </Modal>
  );
}

export default AddSuppliedQuantityModal;
